/* eslint-disable @next/next/no-html-link-for-pages */
'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { SelectInput, SliderInput, DualInputWithSlider } from './FormInputs';
import { CalculatorCard, CalculatorSection } from './CalculatorCard';
import { MetricCard, BreakdownTable, RecommendationBadge } from './ResultsDisplay';
import { LineChart, BarChart } from './Charts';
import { Toast } from './Toast';
// next/link removed for R2 bundle compatibility - use <a> tags instead

// Industry click rates based on research (KnowBe4, Proofpoint)
const INDUSTRY_CLICK_RATES: Record<string, { baseline: number; label: string }> = {
    healthcare: { baseline: 27.8, label: 'Healthcare' },
    financial: { baseline: 23.4, label: 'Financial Services' },
    technology: { baseline: 19.2, label: 'Technology' },
    retail: { baseline: 31.5, label: 'Retail / E-commerce' },
    manufacturing: { baseline: 28.7, label: 'Manufacturing' },
    professional_services: { baseline: 25.3, label: 'Professional Services' },
    education: { baseline: 30.2, label: 'Education' },
    energy: { baseline: 24.6, label: 'Energy & Utilities' },
    hospitality: { baseline: 32.1, label: 'Hospitality & Travel' },
    government: { baseline: 26.4, label: 'Government / Public Sector' },
    general: { baseline: 27.0, label: 'General / Not Listed' }
};

// Cost per phishing incident by severity
const PHISHING_COST_ESTIMATES: { value: number; label: string; description: string }[] = [
    { value: 1500, label: '$1,500 (Low)', description: 'Credential reset, minimal disruption' },
    { value: 5000, label: '$5,000 (Moderate)', description: 'Some data exposure, remediation needed' },
    { value: 15000, label: '$15,000 (Significant)', description: 'Data breach, regulatory notification' },
    { value: 35000, label: '$35,000 (Severe)', description: 'Major breach, legal/compliance costs' },
    { value: 50000, label: '$50,000+ (Critical)', description: 'Ransomware, business interruption' }
];

// Training effectiveness benchmarks
const TRAINING_EFFECTIVENESS = {
    basic: { reduction: 50, label: 'Basic (Annual only)' },
    moderate: { reduction: 65, label: 'Moderate (Quarterly + Simulations)' },
    comprehensive: { reduction: 75, label: 'Comprehensive (Monthly + Simulations)' },
    elite: { reduction: 85, label: 'Elite (Continuous + Targeted)' }
};

// Security Awareness Training result type
interface TrainingROIResult {
    baselineRisk: number;
    postTrainingRisk: number;
    annualRiskReduction: number;
    trainingCost: number;
    netSavings: number;
    roiPercentage: number;
    paybackPeriod: number | null;
    recommendation: 'APPROVE' | 'CONSIDER' | 'DEFER' | 'REJECT';
    yearlyProjection: { year: number; cumulativeSavings: number; cumulativeCost: number; netValue: number }[];
    breakEvenMonth: number | null;
}

export function SecurityAwarenessTrainingTab() {
    const [step, setStep] = useState(1);
    const [showResults, setShowResults] = useState(false);
    const [trainingIndustry, setTrainingIndustry] = useState('general');
    const [employeeCount, setEmployeeCount] = useState(100);
    const [currentClickRate, setCurrentClickRate] = useState(27.0);
    const [targetClickRate, setTargetClickRate] = useState(8.0);
    const [avgPhishingCost, setAvgPhishingCost] = useState(5000);
    const [phishingAttemptsPerEmployee, setPhishingAttemptsPerEmployee] = useState(20);
    const [trainingCostPerUser, setTrainingCostPerUser] = useState(50);
    const [implementationAdminCost, setImplementationAdminCost] = useState(5000);
    const [trainingProgram, setTrainingProgram] = useState<'basic' | 'moderate' | 'comprehensive' | 'elite'>('moderate');
    const [result, setResult] = useState<TrainingROIResult | null>(null);
    const [showToast, setShowToast] = useState(false);

    // Update click rate when training industry changes
    useEffect(() => {
        const industryData = INDUSTRY_CLICK_RATES[trainingIndustry] || INDUSTRY_CLICK_RATES.general;
        setCurrentClickRate(industryData.baseline);
    }, [trainingIndustry]);

    // Update target click rate based on training program
    useEffect(() => {
        const effectivenessData = TRAINING_EFFECTIVENESS[trainingProgram];
        const reductionPercent = effectivenessData.reduction / 100;
        const newTargetRate = currentClickRate * (1 - reductionPercent);
        setTargetClickRate(Math.max(3, parseFloat(newTargetRate.toFixed(1))));
    }, [trainingProgram, currentClickRate]);

    // Calculate Training ROI
    const calculateTrainingROI = useCallback(() => {
        // Baseline risk: employees x attempts x click rate x cost
        const totalPhishingAttempts = employeeCount * phishingAttemptsPerEmployee;
        const baselineSuccessfulAttacks = totalPhishingAttempts * (currentClickRate / 100);
        const baselineRisk = baselineSuccessfulAttacks * avgPhishingCost;

        // Post-training risk
        const postTrainingSuccessfulAttacks = totalPhishingAttempts * (targetClickRate / 100);
        const postTrainingRisk = postTrainingSuccessfulAttacks * avgPhishingCost;

        // Annual savings
        const annualRiskReduction = baselineRisk - postTrainingRisk;

        // Training costs
        const annualTrainingCost = (employeeCount * trainingCostPerUser) + implementationAdminCost;

        // Net savings and ROI
        const netSavings = annualRiskReduction - annualTrainingCost;
        const roiPercentage = annualTrainingCost > 0 ? ((annualRiskReduction - annualTrainingCost) / annualTrainingCost) * 100 : 0;

        // Payback period (in years)
        const paybackPeriod = annualRiskReduction > 0 ? annualTrainingCost / annualRiskReduction : null;

        // Break-even month
        const monthlyRiskReduction = annualRiskReduction / 12;
        const breakEvenMonth = monthlyRiskReduction > 0 ? Math.ceil(annualTrainingCost / monthlyRiskReduction) : null;

        // 3-year projection
        const yearlyProjection = [];
        let cumulativeSavings = 0;
        let cumulativeCost = 0;
        for (let year = 1; year <= 3; year++) {
            cumulativeSavings += annualRiskReduction;
            // First year has implementation cost, subsequent years just training cost
            const yearCost = year === 1
                ? (employeeCount * trainingCostPerUser) + implementationAdminCost
                : employeeCount * trainingCostPerUser;
            cumulativeCost += yearCost;
            yearlyProjection.push({
                year,
                cumulativeSavings,
                cumulativeCost,
                netValue: cumulativeSavings - cumulativeCost
            });
        }

        // Recommendation
        let recommendation: 'APPROVE' | 'CONSIDER' | 'DEFER' | 'REJECT';
        if (roiPercentage >= 100) {
            recommendation = 'APPROVE';
        } else if (roiPercentage >= 50) {
            recommendation = 'CONSIDER';
        } else if (roiPercentage >= 0) {
            recommendation = 'DEFER';
        } else {
            recommendation = 'REJECT';
        }

        setResult({
            baselineRisk,
            postTrainingRisk,
            annualRiskReduction,
            trainingCost: annualTrainingCost,
            netSavings,
            roiPercentage,
            paybackPeriod,
            recommendation,
            yearlyProjection,
            breakEvenMonth
        });
        setShowResults(true);
    }, [employeeCount, phishingAttemptsPerEmployee, currentClickRate, targetClickRate, avgPhishingCost, trainingCostPerUser, implementationAdminCost]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatPercentage = (value: number) => {
        return `${value.toFixed(1)}%`;
    };

    if (showResults && result) {
        return (
            <div className="max-w-7xl mx-auto">
                {/* Back button */}
                <div className="mb-8">
                    <button onClick={() => setShowResults(false)} className="text-purple-500 hover:text-purple-600 font-semibold flex items-center gap-2">
                        ← Recalculate
                    </button>
                </div>

                {/* Recommendation Badge */}
                <div className="flex items-center justify-center mb-8">
                    <RecommendationBadge recommendation={result.recommendation}/>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <MetricCard label="Year 1 ROI" value={formatPercentage(result.roiPercentage)} description="Return on training investment" icon="📈" color={result.roiPercentage > 100 ? 'green' : result.roiPercentage > 0 ? 'blue' : 'red'}/>
                    <MetricCard label="Annual Savings" value={formatCurrency(result.netSavings)} description="Net savings after training costs" icon="💰" color={result.netSavings > 0 ? 'green' : 'red'}/>
                    <MetricCard label="Risk Reduction" value={formatCurrency(result.annualRiskReduction)} description="Annual phishing risk reduction" icon="🛡️" color="blue"/>
                    <MetricCard label="Break-Even" value={result.breakEvenMonth ? `${result.breakEvenMonth} months` : 'N/A'} description="Time to recover investment" icon="⏱️" color="purple"/>
                </div>

                {/* Before/After Risk Comparison */}
                <CalculatorCard className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <span className="text-3xl">🎯</span>
                        Phishing Risk Analysis
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-red-50 rounded-lg p-6 border-2 border-red-200">
                            <div className="text-sm font-semibold text-gray-600 mb-2">Before Training</div>
                            <div className="text-3xl font-bold text-red-600 mb-2">
                                {formatCurrency(result.baselineRisk)}/year
                            </div>
                            <div className="text-sm text-gray-600">
                                At {formatPercentage(currentClickRate)} click rate
                            </div>
                            <div className="text-xs text-gray-500 mt-2">
                                ~{Math.round(employeeCount * phishingAttemptsPerEmployee * (currentClickRate / 100))} successful attacks/year
                            </div>
                        </div>
                        <div className="flex items-center justify-center">
                            <div className="text-4xl text-gray-400">→</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200">
                            <div className="text-sm font-semibold text-gray-600 mb-2">After Training</div>
                            <div className="text-3xl font-bold text-green-600 mb-2">
                                {formatCurrency(result.postTrainingRisk)}/year
                            </div>
                            <div className="text-sm text-gray-600">
                                At {formatPercentage(targetClickRate)} click rate
                            </div>
                            <div className="text-xs text-gray-500 mt-2">
                                ~{Math.round(employeeCount * phishingAttemptsPerEmployee * (targetClickRate / 100))} successful attacks/year
                            </div>
                        </div>
                    </div>

                    {/* Click Rate Improvement Visual */}
                    <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                        <h3 className="font-bold text-gray-900 mb-4">Click Rate Improvement</h3>
                        <div className="flex items-center gap-4">
                            <div className="flex-1">
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-red-600 font-semibold">Before: {formatPercentage(currentClickRate)}</span>
                                    <span className="text-green-600 font-semibold">After: {formatPercentage(targetClickRate)}</span>
                                </div>
                                <div className="h-6 bg-gray-200 rounded-full overflow-hidden relative">
                                    <div
                                        className="h-full bg-gradient-to-r from-red-500 to-green-500 transition-all duration-500"
                                        style={{ width: '100%' }}
                                    />
                                    <div
                                        className="absolute top-0 left-0 h-full bg-red-500 transition-all duration-500"
                                        style={{ width: `${(targetClickRate / currentClickRate) * 100}%` }}
                                    />
                                </div>
                                <div className="text-center mt-2 text-lg font-bold text-purple-600">
                                    {formatPercentage(((currentClickRate - targetClickRate) / currentClickRate) * 100)} reduction in click susceptibility
                                </div>
                            </div>
                        </div>
                    </div>
                </CalculatorCard>

                {/* Cost vs Benefit Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <BreakdownTable title="Training Program Costs" items={[
                        {
                            label: 'Per-User Training Cost',
                            value: formatCurrency(employeeCount * trainingCostPerUser),
                            tooltip: `${employeeCount} employees x ${formatCurrency(trainingCostPerUser)}/user`
                        },
                        {
                            label: 'Implementation & Admin',
                            value: formatCurrency(implementationAdminCost),
                            tooltip: 'Setup, configuration, and ongoing administration costs'
                        }
                    ]} total={{
                        label: 'Total Annual Investment',
                        value: formatCurrency(result.trainingCost)
                    }}/>

                    <BreakdownTable title="Risk Reduction Benefits" items={[
                        {
                            label: 'Baseline Phishing Risk',
                            value: formatCurrency(result.baselineRisk),
                            tooltip: 'Annual expected loss from phishing before training'
                        },
                        {
                            label: 'Post-Training Risk',
                            value: formatCurrency(result.postTrainingRisk),
                            tooltip: 'Reduced annual expected loss after training'
                        }
                    ]} total={{
                        label: 'Annual Risk Reduction',
                        value: formatCurrency(result.annualRiskReduction)
                    }}/>
                </div>

                {/* 3-Year ROI Projection */}
                <CalculatorCard className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <span className="text-2xl">📈</span>
                        3-Year ROI Projection
                    </h3>
                    <p className="text-gray-600 mb-6">
                        This chart shows how your training investment compounds over time. The net value
                        represents cumulative savings minus cumulative costs.
                    </p>
                    <LineChart
                        title="Cumulative Net Value Over Time"
                        data={result.yearlyProjection.map((p) => ({
                            x: `Year ${p.year}`,
                            y: p.netValue
                        }))}
                        xLabel="Year"
                        yLabel="Net Value ($)"
                        yUnit="$"
                        height={350}
                    />
                </CalculatorCard>

                {/* Savings vs Cost Comparison Bar Chart */}
                <CalculatorCard className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="text-2xl">💵</span>
                        Annual Investment vs Return
                    </h3>
                    <BarChart
                        title="Cost vs Benefit Analysis"
                        data={[
                            { label: 'Training Investment', value: result.trainingCost, color: 'bg-red-500' },
                            { label: 'Risk Reduction Value', value: result.annualRiskReduction, color: 'bg-green-500' },
                            { label: 'Net Annual Savings', value: Math.max(0, result.netSavings), color: 'bg-blue-500' }
                        ]}
                        unit=""
                        showValues={true}
                    />
                </CalculatorCard>

                {/* Industry Benchmarks */}
                <CalculatorCard className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="text-2xl">📊</span>
                        Industry Benchmarks & Research
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-bold text-blue-900 mb-2">Phishing Click Rates by Industry</h4>
                            <div className="space-y-2 text-sm">
                                {Object.entries(INDUSTRY_CLICK_RATES).slice(0, 6).map(([key, data]) => (
                                    <div key={key} className="flex justify-between">
                                        <span className="text-gray-700">{data.label}</span>
                                        <span className="font-semibold text-blue-600">{data.baseline}%</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-3">Source: KnowBe4 Phishing Industry Benchmarking Report 2024</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                            <h4 className="font-bold text-green-900 mb-2">Training Effectiveness</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-700">Average click rate reduction</span>
                                    <span className="font-semibold text-green-600">50-80%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-700">Time to see improvement</span>
                                    <span className="font-semibold text-green-600">90 days</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-700">Optimal simulation frequency</span>
                                    <span className="font-semibold text-green-600">Monthly</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-700">Best-in-class click rate</span>
                                    <span className="font-semibold text-green-600">&lt;5%</span>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-3">Source: Proofpoint State of the Phish 2024, SANS Security Awareness Report</p>
                        </div>
                    </div>

                    <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <h4 className="font-bold text-purple-900 mb-2">Key Statistics</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                            <div>
                                <div className="text-2xl font-bold text-purple-600">91%</div>
                                <div className="text-xs text-gray-600">of breaches start with phishing</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-purple-600">$4.76M</div>
                                <div className="text-xs text-gray-600">avg phishing breach cost</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-purple-600">37x</div>
                                <div className="text-xs text-gray-600">typical training ROI</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-purple-600">70%</div>
                                <div className="text-xs text-gray-600">reduction in successful attacks</div>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3 text-center">Sources: Verizon DBIR 2024, IBM Cost of a Data Breach Report 2024</p>
                    </div>
                </CalculatorCard>

                {/* CTA Section */}
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-8 mb-8 text-white">
                    <div className="max-w-3xl mx-auto text-center">
                        {result.recommendation === 'APPROVE' ? (
                            <>
                                <div className="text-5xl mb-4">🎓</div>
                                <h3 className="text-3xl font-bold mb-4">
                                    Ready to Implement Security Awareness Training?
                                </h3>
                                <p className="text-xl text-white/90 mb-6">
                                    Your analysis shows a {formatPercentage(result.roiPercentage)} ROI with {formatCurrency(result.netSavings)} in annual savings. Let us help you choose and deploy the right training platform.
                                </p>
                                <div className="grid md:grid-cols-3 gap-6 mb-8 text-left">
                                    <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                                        <div className="text-2xl mb-2">🎯</div>
                                        <h4 className="font-bold mb-2">Platform Selection</h4>
                                        <p className="text-sm text-white/80">
                                            We evaluate KnowBe4, Proofpoint, and other leading platforms
                                        </p>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                                        <div className="text-2xl mb-2">📊</div>
                                        <h4 className="font-bold mb-2">Baseline Assessment</h4>
                                        <p className="text-sm text-white/80">
                                            Initial phishing simulation to establish your true baseline
                                        </p>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                                        <div className="text-2xl mb-2">📈</div>
                                        <h4 className="font-bold mb-2">Ongoing Management</h4>
                                        <p className="text-sm text-white/80">
                                            Monthly simulations, reporting, and continuous improvement
                                        </p>
                                    </div>
                                </div>
                                <a href={`/contact?source=training_roi_calculator&employees=${employeeCount}&roi=${result.roiPercentage.toFixed(1)}`} className="inline-block bg-white text-purple-600 hover:bg-gray-100 px-10 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg">
                                    Get a Free Training Assessment →
                                </a>
                            </>
                        ) : (
                            <>
                                <div className="text-5xl mb-4">💡</div>
                                <h3 className="text-3xl font-bold mb-4">
                                    Let us Optimize Your Training Program
                                </h3>
                                <p className="text-xl text-white/90 mb-6">
                                    The ROI analysis suggests room for optimization. We can help you find
                                    a more cost-effective approach or improve your risk estimates.
                                </p>
                                <a href="/contact?source=training_roi_calculator" className="inline-block bg-white text-purple-600 hover:bg-gray-100 px-10 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg">
                                    Get Expert Guidance →
                                </a>
                            </>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button onClick={() => {
                        const currentURL = window.location.href;
                        navigator.clipboard.writeText(currentURL);
                        setShowToast(true);
                    }} className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                        <span>🔗</span>
                        <span>Copy Share Link</span>
                    </button>
                    <button onClick={() => setShowResults(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors shadow hover:shadow-lg flex items-center justify-center gap-2">
                        <span>↻</span>
                        <span>Start Over</span>
                    </button>
                </div>

                {showToast && (<Toast message="Link copied! Share this URL to show your ROI calculation." type="success" onClose={() => setShowToast(false)}/>)}
            </div>
        );
    }

    // Training Input Form
    return (
        <div className="max-w-7xl mx-auto">
            {/* Step Indicator */}
            <div className="mb-8">
                <div className="flex items-center justify-between max-w-xl mx-auto">
                    {[1, 2].map((s) => (
                        <React.Fragment key={s}>
                            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-bold ${s === step
                                ? 'bg-purple-500 text-white border-purple-500'
                                : s < step
                                    ? 'bg-purple-500 text-white border-purple-500'
                                    : 'bg-white text-gray-400 border-gray-300'}`}>
                                {s < step ? '✓' : s}
                            </div>
                            {s < 2 && (<div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-purple-500' : 'bg-gray-300'}`}/>)}
                        </React.Fragment>
                    ))}
                </div>
                <div className="flex justify-between max-w-xl mx-auto mt-2 text-xs font-semibold text-gray-600">
                    <span>Organization & Risk</span>
                    <span>Training Program</span>
                </div>
            </div>

            {/* Step 1: Organization & Risk Profile */}
            {step === 1 && (
                <CalculatorCard>
                    <CalculatorSection title="Organization Profile" description="Tell us about your organization and phishing risk" icon="🏢">
                        <SelectInput
                            label="Industry"
                            value={trainingIndustry}
                            onChange={setTrainingIndustry}
                            options={Object.entries(INDUSTRY_CLICK_RATES).map(([key, data]) => ({
                                value: key,
                                label: data.label
                            }))}
                            helpText="Industry affects baseline click rates"
                            required
                        />

                        <DualInputWithSlider
                            label="Number of Employees"
                            value={employeeCount}
                            onChange={setEmployeeCount}
                            min={10}
                            max={50000}
                            helpText="Total employees who will receive training"
                            required
                        />

                        <div className="p-4 bg-blue-50 rounded-lg mb-4">
                            <div className="flex items-start gap-3">
                                <div className="text-2xl">📊</div>
                                <div>
                                    <h4 className="font-bold text-blue-900 mb-1">
                                        Industry Baseline: {formatPercentage(INDUSTRY_CLICK_RATES[trainingIndustry]?.baseline || 27)}
                                    </h4>
                                    <p className="text-sm text-blue-800">
                                        This is the average phishing click rate for {INDUSTRY_CLICK_RATES[trainingIndustry]?.label || 'your industry'} based on KnowBe4 benchmarking data. You can adjust if you have your own data.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <SliderInput
                            label="Current Phishing Click Rate"
                            value={currentClickRate}
                            onChange={setCurrentClickRate}
                            min={5}
                            max={50}
                            step={0.5}
                            unit="%"
                            helpText="Percentage of employees who click phishing links"
                        />
                    </CalculatorSection>

                    <CalculatorSection title="Phishing Risk Factors" description="Estimate your phishing attack exposure and costs" icon="⚠️">
                        <SliderInput
                            label="Phishing Attempts per Employee per Year"
                            value={phishingAttemptsPerEmployee}
                            onChange={setPhishingAttemptsPerEmployee}
                            min={5}
                            max={100}
                            step={1}
                            helpText="Industry average: 10-50 attempts per employee annually"
                        />

                        <SelectInput
                            label="Average Cost per Successful Phishing Attack"
                            value={String(avgPhishingCost)}
                            onChange={(v) => setAvgPhishingCost(parseInt(v))}
                            options={PHISHING_COST_ESTIMATES.map(est => ({
                                value: String(est.value),
                                label: `${est.label} - ${est.description}`
                            }))}
                            helpText="Includes remediation, downtime, and potential breach costs"
                            required
                        />

                        <div className="p-4 bg-orange-50 rounded-lg mt-4">
                            <div className="flex items-start gap-3">
                                <div className="text-2xl">⚠️</div>
                                <div>
                                    <h4 className="font-bold text-orange-900 mb-1">
                                        Estimated Annual Phishing Risk
                                    </h4>
                                    <p className="text-2xl font-bold text-orange-600">
                                        {formatCurrency(employeeCount * phishingAttemptsPerEmployee * (currentClickRate / 100) * avgPhishingCost)}
                                    </p>
                                    <p className="text-sm text-orange-800 mt-1">
                                        {employeeCount} employees x {phishingAttemptsPerEmployee} attempts x {formatPercentage(currentClickRate)} click rate x {formatCurrency(avgPhishingCost)}/incident
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CalculatorSection>

                    <div className="flex justify-end mt-6">
                        <button onClick={() => setStep(2)} className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                            Next: Training Program
                        </button>
                    </div>
                </CalculatorCard>
            )}

            {/* Step 2: Training Program */}
            {step === 2 && (
                <CalculatorCard>
                    <CalculatorSection title="Training Program" description="Configure your security awareness training investment" icon="🎓">
                        <SelectInput
                            label="Training Program Type"
                            value={trainingProgram}
                            onChange={(v) => setTrainingProgram(v as 'basic' | 'moderate' | 'comprehensive' | 'elite')}
                            options={Object.entries(TRAINING_EFFECTIVENESS).map(([key, data]) => ({
                                value: key,
                                label: `${data.label} - ${data.reduction}% click reduction`
                            }))}
                            helpText="More comprehensive programs deliver better results"
                            required
                        />

                        <div className="p-4 bg-purple-50 rounded-lg mb-4">
                            <h4 className="font-bold text-purple-900 mb-2">Program Comparison</h4>
                            <div className="space-y-2 text-sm">
                                <div className={`p-2 rounded ${trainingProgram === 'basic' ? 'bg-purple-100 border-2 border-purple-300' : ''}`}>
                                    <strong>Basic:</strong> Annual training module, no simulations. 50% improvement.
                                </div>
                                <div className={`p-2 rounded ${trainingProgram === 'moderate' ? 'bg-purple-100 border-2 border-purple-300' : ''}`}>
                                    <strong>Moderate:</strong> Quarterly training + monthly phishing simulations. 65% improvement.
                                </div>
                                <div className={`p-2 rounded ${trainingProgram === 'comprehensive' ? 'bg-purple-100 border-2 border-purple-300' : ''}`}>
                                    <strong>Comprehensive:</strong> Monthly micro-training + bi-weekly simulations + reporting. 75% improvement.
                                </div>
                                <div className={`p-2 rounded ${trainingProgram === 'elite' ? 'bg-purple-100 border-2 border-purple-300' : ''}`}>
                                    <strong>Elite:</strong> Continuous training + targeted simulations + gamification + executive reporting. 85% improvement.
                                </div>
                            </div>
                        </div>

                        <DualInputWithSlider
                            label="Training Cost per User (Annual)"
                            value={trainingCostPerUser}
                            onChange={setTrainingCostPerUser}
                            min={10}
                            max={200}
                            prefix="$"
                            helpText="Typical range: $15-75/user for most platforms"
                            required
                        />

                        <DualInputWithSlider
                            label="Implementation & Admin Costs"
                            value={implementationAdminCost}
                            onChange={setImplementationAdminCost}
                            min={0}
                            max={50000}
                            prefix="$"
                            helpText="Setup, integration, and ongoing administration time"
                        />
                    </CalculatorSection>

                    <CalculatorSection title="Expected Results" description="Projected improvement based on your selections" icon="🎯">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                <div className="text-sm text-gray-600">Current Click Rate</div>
                                <div className="text-2xl font-bold text-red-600">{formatPercentage(currentClickRate)}</div>
                            </div>
                            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="text-sm text-gray-600">Target Click Rate</div>
                                <div className="text-2xl font-bold text-green-600">{formatPercentage(targetClickRate)}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {formatPercentage(TRAINING_EFFECTIVENESS[trainingProgram].reduction)} improvement expected
                                </div>
                            </div>
                        </div>

                        <SliderInput
                            label="Adjust Target Click Rate (Optional)"
                            value={targetClickRate}
                            onChange={setTargetClickRate}
                            min={2}
                            max={currentClickRate}
                            step={0.5}
                            unit="%"
                            helpText="Industry best-in-class: 3-5%"
                        />

                        <div className="p-4 bg-blue-50 rounded-lg mt-4">
                            <div className="flex items-start gap-3">
                                <div className="text-2xl">💰</div>
                                <div>
                                    <h4 className="font-bold text-blue-900 mb-1">
                                        Total Annual Training Investment
                                    </h4>
                                    <p className="text-2xl font-bold text-blue-600">
                                        {formatCurrency((employeeCount * trainingCostPerUser) + implementationAdminCost)}
                                    </p>
                                    <p className="text-sm text-blue-800 mt-1">
                                        {formatCurrency(trainingCostPerUser)}/user x {employeeCount} employees + {formatCurrency(implementationAdminCost)} admin
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CalculatorSection>

                    <div className="flex justify-between mt-6">
                        <button onClick={() => setStep(1)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors">
                            Back
                        </button>
                        <button onClick={calculateTrainingROI} className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                            Calculate Training ROI
                        </button>
                    </div>
                </CalculatorCard>
            )}
        </div>
    );
}
